'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { adjustMarginActionBunja } from '@flipster/features/src/shared/trade-actions/adjust-margin/state';
import { OpenPosition } from '@flipster/r230522/original/position';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { PageType, PositionEvent, PositionType } from '@flipster/utils/segment/events/position';

import { adjustMarginStateBunja } from './state';

interface Props {
  currentPosition: OpenPosition;
  page: PageType;
  onClose: () => void;
  onSuccess: () => void;
}

const AdjustMarginFooter = ({ currentPosition, page, onClose, onSuccess }: Props) => {
  const transCommon = useTranslations('Common.r250401');
  const transPositionAddFunds = useTranslations('Position_add_funds.r250401');

  const { tabAtom, amountAtom, dirtyAtom, isValidAtom, maxAmountAtom, symbol, slot } = useBunja(adjustMarginStateBunja);

  const { adjustMargin, isAdjustMarginLoadingAtom } = useBunja(adjustMarginActionBunja);
  const isAdjustMarginLoading = useAtomValue(isAdjustMarginLoadingAtom);

  const tab = useAtomValue(tabAtom);
  const maxAmount = useAtomValue(maxAmountAtom);
  const amount = useAtomValue(amountAtom);
  const isDirty = useAtomValue(dirtyAtom).amount;
  const isValid = useAtomValue(isValidAtom);

  const { fundsInvested } = currentPosition;

  const newFundsInvested =
    tab === 'add' ? Num(fundsInvested).plus(amount || '0') : Num(fundsInvested).minus(amount || '0');

  if (Num(maxAmount).isEqualTo(0)) return <Button onClick={onClose} label={transCommon('Confirm')} />;

  return (
    <Button
      onClick={async () => {
        try {
          await adjustMargin({ symbol, slot, amount: newFundsInvested.toString() });
          snackbar.success({
            message:
              tab === 'add' ? transPositionAddFunds('Funds_added_toast') : transPositionAddFunds('Funds_removed_toast'),
          });
          onSuccess();
          segment.track(PositionEvent.AdjustMarginConfirmClick, {
            margin_usdt: Number(currentPosition.fundsInvested),
            symbol: currentPosition.symbol,
            unrealized_pnl: Number(currentPosition.unrealizedPnl),
            leverage: currentPosition.initLeverage,
            trade_type: 'perpetual',
            page_type: page,
            position: currentPosition.side.toLowerCase() as PositionType,
            adjust_margin_usdt: Number(newFundsInvested),
          });
        } catch {
          // Do nothing
        }
      }}
      isLoading={isAdjustMarginLoading}
      disabled={!isDirty || !isValid}
      label={transCommon('Confirm')}
    />
  );
};

export default AdjustMarginFooter;
